<script setup lang="ts">
import { GpBrandLoading, GpBtn } from '@gportal/uikit';
import CreateProjectImageModal from '@panel/components/project/images/CreateProjectImageModal.vue';
import ProjectImagePanel from '@panel/components/project/images/ProjectImagePanel.vue';
import useProjectViewTitle from '@panel/composables/useProjectViewTitle';
import { type ServiceError } from 'gpcore-apikit/grpc/types';
import { useImageStore } from 'gpcore-apikit/store/pinia/image';
import { useProjectStore } from 'gpcore-apikit/store/pinia/project';
import type { BasicImage } from 'gpcore-grpc/gpcore/api/cloud/v1/image_pb';
import { onMounted, ref } from 'vue';
import ImagesLayout from '@panel/layouts/Layout.Images.vue';
import { faPlus } from '@fortawesome/pro-solid-svg-icons';
import useNotifications from 'gpcore-uikit/composables/useNotifications';
import ExternalLink from 'gpcore-uikit/components/Component.ExternalLink.vue';

useProjectViewTitle('Project Images');

const projectImages = ref<Array<BasicImage>>([]);
const isLoadingProjectImages = ref(false);
const projectStore = useProjectStore();
const imageStore = useImageStore();
const { addNotification } = useNotifications();

const reloadProjectImages = async () => {
  isLoadingProjectImages.value = true;
  try {
    const response = await imageStore.reloadProjectImages(projectStore.projectId);
    projectImages.value = response.images.map((i: BasicImage) => i);
  } catch (error) {
    addNotification({
      type: 'danger',
      message: (error as ServiceError).message,
    });
  } finally {
    isLoadingProjectImages.value = false;
  }
};

const isCreateModalOpen = ref(false);
const onProjectImageCreated = () => {
  reloadProjectImages();
  isCreateModalOpen.value = false;
};
onMounted(() => {
  reloadProjectImages();
});
</script>

<template>
  <ImagesLayout>
    <template #buttons>
      <GpBtn size="sm" :icon="faPlus" label="Create" @click="isCreateModalOpen = true" />
    </template>
    <template #content>
      <p>
        GPCORE provides a variation of common distribution images for Linux and Windows, from which
        you can choose from. In addition to that, it is possible create your own images (always
        bound to the project), which can then be used to create or reinstall nodes from. These
        images need to be in a disk image format (qcow2 or raw). For more information on that,
        please refer to
        <ExternalLink :url="`${$config.docs.url}/guides/custom-images/`"
          >the documentation</ExternalLink
        >.
      </p>

      <h2 class="tw-mt-8">Project images</h2>
      <GpBrandLoading
        v-if="isLoadingProjectImages"
        class="tw-mx-auto"
        message="Loading project images ..."
      />

      <GpBrandLoading
        v-else-if="projectImages.length === 0"
        class="tw-mx-auto"
        :animate="false"
        message="No images found."
      />

      <template v-else>
        <transition-group name="fade">
          <ProjectImagePanel
            v-for="(image, index) in projectImages"
            class="tw-mt-4"
            :key="image.id"
            :image="image"
            @deleted="reloadProjectImages"
            :style="{ 'transition-delay': `${index * 100}ms` }"
          />
        </transition-group>
      </template>
      <CreateProjectImageModal v-model:show="isCreateModalOpen" @created="onProjectImageCreated" />
    </template>
  </ImagesLayout>
</template>
